export type AudioWorkletMessage = {
    type: 'AUDIO_CHUNK';
    buffer: Float32Array;
};

// Resample audio buffer to target sample rate (16kHz for Gemini)
export function resampleAudioBuffer(
    audioBuffer: Float32Array,
    origSampleRate: number,
    targetSampleRate: number
): Float32Array {
    if (origSampleRate === targetSampleRate) return audioBuffer;

    const ratio = origSampleRate / targetSampleRate;
    const newLength = Math.round(audioBuffer.length / ratio);
    const result = new Float32Array(newLength);

    let offsetResult = 0;
    let offsetSource = 0;

    while (offsetResult < result.length) {
        const nextOffsetSource = Math.round((offsetResult + 1) * ratio);

        // Linear interpolation
        let accum = 0;
        let count = 0;

        // Simple downsampling: average the source samples
        for (let i = offsetSource; i < nextOffsetSource && i < audioBuffer.length; i++) {
            accum += audioBuffer[i];
            count++;
        }

        result[offsetResult] = count > 0 ? accum / count : 0;

        offsetResult++;
        offsetSource = nextOffsetSource;
    }

    return result;
}

// Convert Float32 (-1.0 to 1.0) to Int16 PCM via Base64
export function float32ToPcm16Base64(float32: Float32Array): string {
    const int16Array = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]));
        int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    // Convert to binary string
    let binary = '';
    const bytes = new Uint8Array(int16Array.buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }

    return btoa(binary);
}

// Convert Base64 PCM16 back to Float32 for playback
export function pcm16Base64ToFloat32(base64: string): Float32Array {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / 32768.0;
    }
    return float32;
}
